import React from 'react';
import styled from 'styled-components';

import { _ } from '@splunk/ui-utils/i18n';
import { variables, mixins } from '@splunk/themes';
import type { Mode } from '@splunk/dashboard-types';

import Tooltip from '@splunk/react-ui/Tooltip';

export const LINE_HEIGHT = '28px';
const fontSize = '24px';

const Title = styled.h2<{ isExpanded: boolean }>`
    ${mixins.reset('block')};
    ${(props) => (props.isExpanded ? 'min-height' : 'height')}: LINE_HEIGHT;
    max-width: 100%;
    margin: 0;
    padding: 1px 12px 0px 9px;
    box-sizing: inherit;
    line-height: ${LINE_HEIGHT};
    font-size: ${fontSize};
    font-weight: 500;
    color: ${variables.contentColorActive};
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: ${(props) => (props.isExpanded ? 'normal' : 'nowrap')};
`;

const TitleInput = styled.input`
    ${mixins.reset('block')};
    height: ${LINE_HEIGHT};
    width: 100%;
    margin: 0;
    padding: 3px 16px 0px 8px; // Left & Top Padding is intentionally different than the above component for alignment.
    line-height: ${LINE_HEIGHT};
    box-sizing: border-box;
    font-size: ${fontSize};
    font-weight: 500;
    color: ${variables.contentColorActive};
    box-shadow: none;
    outline: none;
    background-color: transparent;
    border: transparent;

    &:hover,
    &:focus {
        background-color: ${variables.interactiveColorOverlayHover};
        border-style: solid;
        border-width: 1px;
        border-color: ${variables.interactiveColorBorderHover};
        border-radius: 4px;
    }
`;

const tooltipStyle = { flexGrow: 1 };

interface TitleProps {
    handleBlur: React.FocusEventHandler;
    handleChange: React.FormEventHandler;
    handleKeyDown: React.KeyboardEventHandler;
    isExpanded?: boolean;
    mode: Mode;
    value?: string;
}

const TitleInputWrapper = React.forwardRef<
    HTMLInputElement,
    React.ComponentProps<typeof TitleInput> & { describedBy: string }
>(({ describedBy, ...props }, ref) => (
    <TitleInput ref={ref} aria-labelledby={describedBy} {...props} />
));

const DashboardTitle = React.forwardRef<
    HTMLDivElement | HTMLInputElement,
    TitleProps
>(
    (
        {
            handleBlur,
            handleChange,
            handleKeyDown,
            isExpanded = false,
            mode,
            value = '',
        },
        ref
    ): JSX.Element => {
        if (mode === 'edit') {
            return (
                <Tooltip
                    content={_('Dashboard title')}
                    inline={false}
                    style={tooltipStyle}
                >
                    <TitleInputWrapper
                        ref={ref as React.MutableRefObject<HTMLInputElement>}
                        data-test="dashboard-title-input"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={_('Untitled Dashboard')}
                        value={value}
                    />
                </Tooltip>
            );
        }

        return (
            <Title
                ref={ref as React.MutableRefObject<HTMLDivElement>}
                isExpanded={isExpanded}
                data-test="dashboard-title"
            >
                {value}
            </Title>
        );
    }
);

export default React.memo(DashboardTitle);
